import React, { useMemo, useState } from 'react';
import { HDate, gematriya } from '@hebcal/core';

/**
 * Date input with a לועזי/עברי toggle. Gregorian mode is a native date input
 * with the Hebrew date shown underneath; Hebrew mode is day/month/year selects
 * that convert to a Gregorian ISO date on change (the stored value is always
 * Gregorian yyyy-mm-dd, so callers keep a single canonical format).
 */

// hebcal month numbers, ordered for display from Tishrei (school-year order).
const MONTHS_COMMON: { m: number; l: string }[] = [
  { m: 7, l: 'תשרי' }, { m: 8, l: 'חשון' }, { m: 9, l: 'כסלו' }, { m: 10, l: 'טבת' },
  { m: 11, l: 'שבט' }, { m: 12, l: 'אדר' },
  { m: 1, l: 'ניסן' }, { m: 2, l: 'אייר' }, { m: 3, l: 'סיון' }, { m: 4, l: 'תמוז' },
  { m: 5, l: 'אב' }, { m: 6, l: 'אלול' },
];
const MONTHS_LEAP: { m: number; l: string }[] = [
  { m: 7, l: 'תשרי' }, { m: 8, l: 'חשון' }, { m: 9, l: 'כסלו' }, { m: 10, l: 'טבת' },
  { m: 11, l: 'שבט' }, { m: 12, l: "אדר א'" }, { m: 13, l: "אדר ב'" },
  { m: 1, l: 'ניסן' }, { m: 2, l: 'אייר' }, { m: 3, l: 'סיון' }, { m: 4, l: 'תמוז' },
  { m: 5, l: 'אב' }, { m: 6, l: 'אלול' },
];

const toIso = (d: Date) => d.toLocaleDateString('en-CA');

const hebrewLabel = (iso: string): string => {
  try {
    const hd = new HDate(new Date(`${iso}T12:00:00`));
    const months = HDate.isLeapYear(hd.getFullYear()) ? MONTHS_LEAP : MONTHS_COMMON;
    const monthName = months.find(x => x.m === hd.getMonth())?.l || hd.getMonthName();
    return `${gematriya(hd.getDate())} ${monthName} ${gematriya(hd.getFullYear())}`;
  } catch { return ''; }
};

const HebrewDateInput: React.FC<{
  value: string;                       // Gregorian ISO yyyy-mm-dd ('' = empty)
  onChange: (iso: string) => void;
  className?: string;                  // applied to the inputs/selects
}> = ({ value, onChange, className }) => {
  const [mode, setMode] = useState<'greg' | 'hebrew'>('greg');

  // Hebrew-mode working parts, kept in sync with the current value (or today) —
  // without the resync, toggling to עברי after typing a Gregorian date would
  // show today's Hebrew date and silently overwrite the stored value on touch.
  const seed = useMemo(() => {
    try { return new HDate(value ? new Date(`${value}T12:00:00`) : new Date()); }
    catch { return new HDate(new Date()); }
  }, [value]);
  const [hy, setHy] = useState(seed.getFullYear());
  const [hm, setHm] = useState(seed.getMonth());
  const [hd, setHd] = useState(seed.getDate());
  React.useEffect(() => { setHy(seed.getFullYear()); setHm(seed.getMonth()); setHd(seed.getDate()); }, [seed]);

  const months = HDate.isLeapYear(hy) ? MONTHS_LEAP : MONTHS_COMMON;
  const daysInMonth = (() => { try { return HDate.daysInMonth(hm, hy); } catch { return 30; } })();

  const emit = (y: number, m: number, d: number) => {
    try {
      // month may not exist in the new year (Adar II) or day may overflow — clamp
      const mm = HDate.isLeapYear(y) ? m : (m === 13 ? 12 : m);
      const dd = Math.min(d, HDate.daysInMonth(mm, y));
      onChange(toIso(new HDate(dd, mm, y).greg()));
    } catch { /* keep previous value */ }
  };

  const yearNow = new HDate(new Date()).getFullYear();
  const years = Array.from({ length: 121 }, (_, i) => yearNow + 1 - i); // this year+1 back 120y

  const sel = className || 'w-full px-2 py-2 text-sm border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600';

  return (
    <div>
      <div className="flex items-center gap-2">
        {mode === 'greg' ? (
          <input type="date" className={sel} value={value} onChange={e => onChange(e.target.value)} />
        ) : (
          <div className="flex gap-1 flex-1">
            <select className={sel} value={hd} onChange={e => { const d = Number(e.target.value); setHd(d); emit(hy, hm, d); }}>
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => <option key={d} value={d}>{gematriya(d)}</option>)}
            </select>
            <select className={sel} value={hm} onChange={e => { const m = Number(e.target.value); setHm(m); emit(hy, m, hd); }}>
              {months.map(x => <option key={x.m} value={x.m}>{x.l}</option>)}
            </select>
            <select className={sel} value={hy} onChange={e => { const y = Number(e.target.value); setHy(y); emit(y, hm, hd); }}>
              {years.map(y => <option key={y} value={y}>{gematriya(y)}</option>)}
            </select>
          </div>
        )}
        <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 flex-shrink-0">
          {([['greg', 'לועזי'], ['hebrew', 'עברי']] as const).map(([v, l]) => (
            <button key={v} type="button" onClick={() => setMode(v)}
              className={`px-2 py-1 text-[11px] font-semibold rounded-md transition ${mode === v ? 'bg-white dark:bg-gray-700 text-indigo-700 shadow-sm' : 'text-gray-500'}`}>{l}</button>
          ))}
        </div>
      </div>
      {value && mode === 'greg' && <p className="text-[11px] text-gray-400 mt-1">{hebrewLabel(value)}</p>}
      {value && mode === 'hebrew' && <p className="text-[11px] text-gray-400 mt-1">{new Date(`${value}T12:00:00`).toLocaleDateString('he-IL')}</p>}
    </div>
  );
};

export default HebrewDateInput;
